import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment/moment';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { IoMailOutline } from 'react-icons/io5';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import LeaseApiService from '../../services/apiService';
import { endPoint } from '../../EndPoints/endPoint';
import Pagination from '../pagination/pagination';
import '../pagination/pagination.css';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';

const ITEMS_PER_PAGE = 100;
// Same reporting-server bulk-email mechanism used by instalmentInvoiceListing.js
// and leaseGeneralInvoice/listing.js - a hidden form POSTs to the report
// server in a popup, which builds the invoice PDF and queues the email
// itself, rather than the frontend building/uploading a PDF and posting to
// EMAIL_QUEUE_SEND directly.
const REACT_APP_REPORTS = process.env.REACT_APP_REPORTS_URL;

const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });
const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};

const invoiceTypeOptions = [
    { value: 'Installment', label: 'Installment Invoice' },
    { value: 'General', label: 'General Invoice' },
];

// Both invoice list APIs return differently-shaped rows (voucherNo/postingDate
// vs invoiceNo/invoiceDate, no recordId on Installment, no tenantEmailId on
// General) - normalize to one shape so the grid/columns/print/email logic
// below doesn't need to branch on invoice type at all.
//
// `key` doubles as AgGridReact's getRowId, which requires every row to have a
// genuinely unique id - a row missing tranId/invoiceNo (or two rows sharing
// them) used to collide, and a duplicate row id is a known cause of a row
// rendering blank and of selection state getting corrupted for it. Folding
// the row's own index into the key makes it unique within the fetched page
// no matter what the underlying data looks like.
const mapInstallmentRow = (item, index) => ({
    key: `INST-${index}-${item?.tranId ?? 'x'}-${item?.invoiceNo ?? 'x'}`,
    invoiceType: 'Installment',
    recordId: item?.tranId,
    leaseNo: item?.leaseNo || '',
    invoiceNo: item?.invoiceNo || '',
    invoiceDate: item?.invoiceDate || '',
    tenantNo: item?.tenantNo || '',
    tenantName: item?.tenantName || '',
    propertyNo: item?.propertyNo || '',
    propertyName: item?.propertyName || '',
    unitNo: item?.unitNo || '',
    amount: item?.amount,
    tenantEmailId: item?.tenantEmailId || '',
});

const mapGeneralRow = (item, index) => ({
    key: `GEN-${index}-${item?.recordId ?? 'x'}-${item?.voucherNo ?? 'x'}`,
    invoiceType: 'General',
    recordId: item?.recordId,
    leaseNo: item?.leaseNo || '',
    invoiceNo: item?.voucherNo || '',
    invoiceDate: item?.postingDate || '',
    tenantNo: item?.tenantNo || '',
    tenantName: item?.tenantName || '',
    propertyNo: item?.propertyNo || '',
    propertyName: item?.propertyName || '',
    unitNo: item?.unitNo || '',
    amount: item?.amount,
    tenantEmailId: '', // not on the General Invoice list DTO - filled in via enrichMissingEmails
});

// The backend list APIs can come back with the same invoice more than once
// (e.g. one row per installment schedule line sharing the same invoice no) -
// collapse those to one row per invoice, keeping the first occurrence. This
// also matters for uploads: without it, the same Lease No got uploaded
// multiple times in one page load, and the server's own collision-avoidance
// renaming (appending a number) kicked in because multiple uploads targeted
// the same "leaseNo.pdf" name at once.
const dedupeInvoiceRows = (rows) => {
    const seen = new Set();
    return rows.filter((row) => {
        const dedupeKey = row.invoiceNo || `${row.leaseNo}|${row.recordId}`;
        if (seen.has(dedupeKey)) return false;
        seen.add(dedupeKey);
        return true;
    });
};

const dateFormatter = (params) => {
    if (!params.value) return '';
    const date = moment(params.value);
    return date.isValid() ? date.format('DD-MMM-YYYY') : '';
};
const dateComparator = (date1, date2) => {
    const momentDate1 = moment(date1);
    const momentDate2 = moment(date2);
    return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
};
const formatDat = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const InvoiceListingEmail = () => {
    const apiService = new LeaseApiService();
    const { currencyPrecision } = useCurrency();
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [pageSize, setPageSize] = useState(50);
    const [pagination, setPagination] = useState();
    const [FilterTab, setFilterTab] = useState(true);
    const [customerList, setCustomerList] = useState([]);
    const [leaseList, setLeaseList] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    // Per-row state of the background "generate + save invoice PDF" step that
    // runs for the whole list on every page load/search (see
    // uploadInvoicesForRows): 'generating' | 'ready' | 'failed'.
    const [attachmentStatus, setAttachmentStatus] = useState({});

    const [formData, setFormData] = useState({
        invoiceType: invoiceTypeOptions[0],
        leaseNo: null,
        customerNo: null,
        customerName: null,
        from: '',
        to: '',
    });

    // Populated once a row's generated PDF has been saved server-side (see
    // generateAndSaveInvoicePdf) - Send Email reads the saved path from here.
    const attachmentPathByKeyRef = useRef({});

    useEffect(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const initialFrom = formatDat(startOfMonth);
        const initialTo = formatDat(endOfMonth);
        setFormData((prev) => ({ ...prev, from: initialFrom, to: initialTo }));
        fetchCustomerOptions();
        fetchLeaseOptions(invoiceTypeOptions[0].value);
        getItems(1, { from: initialFrom, to: initialTo, invoiceType: invoiceTypeOptions[0] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redraw the "Attachment" status cell after each status change - runs
    // post-render so the grid's valueGetter closure sees the new state.
    useEffect(() => {
        gridApi?.refreshCells({ force: true });
    }, [attachmentStatus, gridApi]);

    const fetchCustomerOptions = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchCustomers');
            const res = await apiService.getApiParamater('LEASE_REPORTS_MULTIDROP', `?${params.toString()}`);
            setCustomerList(res?.data?.dropdowns?.LRAgreeSearchCustomers || []);
        } catch (error) {
            console.error('Error fetching customer dropdown:', error);
        }
    };

    const fetchLeaseOptions = async (invoiceTypeValue) => {
        try {
            const params = new URLSearchParams();
            if (invoiceTypeValue === 'General') {
                params.append('dropdownTypes', 'LGISerLeaseNo');
                const res = await apiService.getApiParamater('GET_GENERAL_MULTI', `?${params.toString()}`);
                setLeaseList(res?.data?.dropdowns?.LGISerLeaseNo || []);
            } else {
                params.append('dropdownTypes', 'LIISerLeaseNo');
                const res = await apiService.getApiParamater('GET_INSTALL_INVOICE_DROP', `?${params.toString()}`);
                setLeaseList(res?.data?.dropdowns?.LIISerLeaseNo || []);
            }
        } catch (error) {
            console.error('Error fetching lease dropdown:', error);
            setLeaseList([]);
        }
    };

    // General Invoice rows don't carry a tenant email at all; Installment rows
    // sometimes come back blank too - backfill both the same way the existing
    // installmentInvoiceEmail/listing.js page does, by looking the tenant up
    // per distinct LeaseNo.
    const enrichMissingEmails = async (rows) => {
        const leaseNosNeeding = Array.from(new Set(rows.filter((r) => !r.tenantEmailId && r.leaseNo).map((r) => r.leaseNo)));
        if (!leaseNosNeeding.length) return rows;
        const emailByLease = {};
        await Promise.all(
            leaseNosNeeding.map(async (leaseNo) => {
                try {
                    const params = new URLSearchParams();
                    params.append('Action', 'GetCustomerDetailsByLeaseNo');
                    params.append('LeaseNo', leaseNo);
                    const res = await apiService.getApiParamater('LEASE_RECEIPT_COMMON', `?${params.toString()}`);
                    emailByLease[leaseNo] = res?.data?.result?.data?.EmailId || '';
                } catch (error) {
                    console.error(`Error loading tenant email for lease ${leaseNo}:`, error);
                    emailByLease[leaseNo] = '';
                }
            }),
        );
        return rows.map((row) => (row.tenantEmailId ? row : { ...row, tenantEmailId: emailByLease[row.leaseNo] || '' }));
    };

    const getItems = async (newPageNo = pagination?.currentPage, overrides = {}) => {
        const effective = { ...formData, ...overrides };
        try {
            setSearching(true);
            const params = new URLSearchParams();
            params.append('PageNo', newPageNo || 1);
            params.append('PageSize', pageSize || 50);
            params.append('PropertyNo', 'All');
            params.append('UnitNo', 'All');
            params.append('LeaseNo', effective?.leaseNo?.label || 'All');
            params.append('TenantNo', effective?.customerNo?.label || 'All');
            params.append('FromDate', effective.from);
            params.append('ToDate', effective.to);

            let rows = [];
            if (effective.invoiceType?.value === 'General') {
                const res = await apiService.getApiParamater('GET_GENERAL_LIST', `?${params.toString()}`);
                rows = (res?.data?.result || []).map(mapGeneralRow);
                setPagination(res?.data?.paginationData);
            } else {
                params.append('Mode', 'EDIT');
                params.append('UnitType', 'All');
                params.append('ShowOther', true);
                const res = await apiService.getApiParamater('GET_INSTALL_INVOICE_LIST', `?${params.toString()}`);
                rows = (res?.data?.result || []).filter((item) => item?.tranId != 0).map(mapInstallmentRow);
                setPagination(res?.data?.paginationData);
            }
            rows = dedupeInvoiceRows(rows);

            setRowData(rows);
            setSelectedRows([]);
            setLoading(false);
            setSearching(false);

            const enriched = await enrichMissingEmails(rows);
            setRowData(enriched);
            uploadInvoicesForRows(enriched);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setRowData([]);
            setLoading(false);
            setSearching(false);
        }
    };

    const handleInvoiceTypeChange = (selectedOption) => {
        setFormData((prev) => ({ ...prev, invoiceType: selectedOption, leaseNo: null }));
        fetchLeaseOptions(selectedOption?.value);
        getItems(1, { invoiceType: selectedOption, leaseNo: null });
    };

    const handleDropDownChange = ({ name, value }) => {
        if (name === 'customerNo') {
            setFormData((prev) => ({
                ...prev,
                customerNo: value,
                customerName: value ? { label: value?.value?.text, value: value?.value } : null,
            }));
        } else if (name === 'customerName') {
            setFormData((prev) => ({
                ...prev,
                customerName: value,
                customerNo: value ? { label: value?.value?.value, value: value?.value } : null,
            }));
        } else if (name === 'leaseNo') {
            setFormData((prev) => ({ ...prev, leaseNo: value }));
        }
    };

    const handleDateChange = ({ name, value }) => {
        const updatedValue = typeof value === 'string' ? value : formatDat(value);
        setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    };

    const handleClearFilters = () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const initialFrom = formatDat(startOfMonth);
        const initialTo = formatDat(endOfMonth);
        setFormData({
            invoiceType: invoiceTypeOptions[0],
            leaseNo: null,
            customerNo: null,
            customerName: null,
            from: initialFrom,
            to: initialTo,
        });
        fetchLeaseOptions(invoiceTypeOptions[0].value);
        getItems(1, { invoiceType: invoiceTypeOptions[0], leaseNo: null, customerNo: null, from: initialFrom, to: initialTo });
    };

    const loadCustomerNoOptions = (search, loadedOptions, { page }) => {
        let filtered = customerList;
        if (search) {
            filtered = customerList.filter(
                (c) => c.value?.toLowerCase().includes(search.toLowerCase()) || c.text?.toLowerCase().includes(search.toLowerCase()),
            );
        }
        const start = page * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        const chunk = filtered.slice(start, end);
        return Promise.resolve({
            options: chunk.map((c) => ({ value: c, label: c.value })),
            hasMore: end < filtered.length,
            additional: { page: page + 1 },
        });
    };

    const loadCustomerNameOptions = (search, loadedOptions, { page }) => {
        let filtered = customerList;
        if (search) {
            filtered = customerList.filter(
                (c) => c.value?.toLowerCase().includes(search.toLowerCase()) || c.text?.toLowerCase().includes(search.toLowerCase()),
            );
        }
        const start = page * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        const chunk = filtered.slice(start, end);
        return Promise.resolve({
            options: chunk.map((c) => ({ value: c, label: c.text })),
            hasMore: end < filtered.length,
            additional: { page: page + 1 },
        });
    };

    // Uploads an already-generated invoice PDF to the SAME server that sends
    // the email (LEASE_FINANCE_BASEURL / UPLOAD_LEASE_INVOICE), not the main
    // lease API's own FileUpload - that saves to a different server's disk
    // entirely, which is why the emailqueue backend could never find the file
    // it was told to attach (attachments came through, but empty). Named by
    // Lease No only, per requirement (with rows deduped there's only ever one
    // upload per lease per page load, so no filename collision).
    const saveInvoicePdfToServer = async (blob, safeLeaseNo) => {
        const token = localStorage.getItem('Token');
        const db = localStorage.getItem('DataBase');
        const fileName = `${safeLeaseNo}.pdf`;
        const formData = new FormData();
        formData.append('files', blob, fileName);
        const response = await axios.post(endPoint.UPLOAD_LEASE_INVOICE, formData, {
            headers: {
                accept: 'text/plain',
                apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
                Authorization: `Bearer ${token}`,
                db,
            },
        });
        console.log('Lease invoice upload response:', response?.data);
    };

    // Confirmed static file URL - wwwroot/uploads/finance/lease is served
    // directly at the site root, so this is just BASEURL + leaseNo + .pdf,
    // no extra lookup call needed (getfullurl's response was unverified and
    // this is the confirmed-correct format instead).
    const getLeaseInvoiceFullUrl = (safeLeaseNo) => `${endPoint.LEASE_INVOICE_FILE_BASE}/${safeLeaseNo}.pdf`;

    // The upload API has no matching "list what's already there" endpoint, so
    // the only way to know a lease's invoice was already uploaded (and skip
    // re-uploading it, per requirement) is to remember it ourselves. This is
    // browser-local only - it prevents re-upload on reloads/re-searches in
    // this same browser, not true server-side dedup.
    // v2: bumped from uploadedLeaseInvoicePaths because that key held bare
    // filenames from before LEASE_INVOICE_FOLDER was added - stale entries
    // under the old key would still point at a path that doesn't resolve.
    // v3: now stores the resolved getfullurl result, not a guessed relative path.
    const UPLOADED_LEASE_INVOICES_KEY = 'uploadedLeaseInvoicePaths_v3';
    const getUploadedLeaseInvoiceMap = () => {
        try {
            const raw = localStorage.getItem(UPLOADED_LEASE_INVOICES_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };
    const markLeaseInvoiceUploaded = (leaseNo, savedPath) => {
        if (!leaseNo || !savedPath) return;
        try {
            const map = getUploadedLeaseInvoiceMap();
            map[leaseNo] = savedPath;
            localStorage.setItem(UPLOADED_LEASE_INVOICES_KEY, JSON.stringify(map));
        } catch (error) {
            console.error('Error persisting uploaded lease invoice record:', error);
        }
    };

    // Builds a simple invoice PDF from the row's own data. The official report
    // (REACT_APP_REPORTS_URL) can't be fetched from here - it's cross-origin
    // and confirmed (via DevTools: ERR_FAILED 200) to have no CORS headers, so
    // the browser blocks reading its response no matter how it's requested.
    // Fixing that needs a server-side proxy on an API we control; until that
    // exists, this generates our own PDF instead so Send Email has a real
    // saved file to attach.
    const buildInvoicePdfBlob = (row) => {
        const doc = new jsPDF('portrait', 'pt', 'A4');
        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, 'F');
        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text(`${row?.invoiceType || 'Invoice'}`, 40, 32);

        doc.setTextColor(0);
        doc.autoTable({
            startY: 70,
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 6 },
            headStyles: { fillColor: [110, 81, 153], textColor: 255 },
            head: [['Field', 'Value']],
            body: [
                ['Invoice No', row?.invoiceNo || '-'],
                ['Invoice Date', row?.invoiceDate ? moment(row.invoiceDate).format('DD-MMM-YYYY') : '-'],
                ['Lease No', row?.leaseNo || '-'],
                ['Customer No', row?.tenantNo || '-'],
                ['Customer Name', row?.tenantName || '-'],
                ['Property Name', row?.propertyName || '-'],
                ['Unit No', row?.unitNo || '-'],
                ['Amount', row?.amount ?? '-'],
                ['Tenant Email', row?.tenantEmailId || '-'],
            ],
        });
        return doc.output('blob');
    };

    // Turns any error (a thrown Error, or an AxiosError with a server response)
    // into one short, human-readable line, so the toast below can show the
    // real reason directly instead of a generic "couldn't generate" message
    // that needs a DevTools trip to explain.
    const describeError = (error) => {
        if (error?.response) {
            const data = error.response.data;
            const serverMessage =
                (typeof data === 'string' && data) ||
                data?.message ||
                data?.title ||
                (Array.isArray(data?.errors) ? data.errors.join('; ') : null);
            return `HTTP ${error.response.status}${serverMessage ? ` - ${serverMessage}` : ''}`;
        }
        if (error?.request) return 'No response from server (network error / unreachable / CORS)';
        return error?.message || String(error);
    };

    const generateAndSaveInvoicePdf = async (row) => {
        setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'generating' }));

        let pdfBlob;
        try {
            pdfBlob = buildInvoicePdfBlob(row);
        } catch (error) {
            console.error(`Error building invoice PDF for ${row.invoiceNo || row.leaseNo}:`, error);
            setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'failed' }));
            toast.error(`Could not build invoice PDF for ${row.invoiceNo || row.leaseNo}: ${describeError(error)}`);
            return;
        }

        const safeLeaseNo = String(row?.leaseNo || row?.invoiceNo || row?.recordId || 'file').replace(/[^a-zA-Z0-9.\-_]/g, '_');

        try {
            await saveInvoicePdfToServer(pdfBlob, safeLeaseNo);
        } catch (error) {
            console.error(`Error uploading invoice PDF for ${row.invoiceNo || row.leaseNo}:`, error);
            setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'failed' }));
            toast.error(`Could not upload invoice PDF for ${row.invoiceNo || row.leaseNo}: ${describeError(error)}`);
            return;
        }

        try {
            const fullUrl = await getLeaseInvoiceFullUrl(safeLeaseNo);
            if (!fullUrl) {
                console.error('getfullurl succeeded but returned no url for', row.invoiceNo || row.leaseNo);
                setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'failed' }));
                toast.error(`Invoice PDF uploaded for ${row.invoiceNo || row.leaseNo}, but couldn't resolve its attachable URL - check console log for the raw response.`);
                return;
            }
            attachmentPathByKeyRef.current[row.key] = fullUrl;
            markLeaseInvoiceUploaded(row.leaseNo, fullUrl);
            setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'ready' }));
        } catch (error) {
            console.error(`Error resolving invoice PDF url for ${row.invoiceNo || row.leaseNo}:`, error);
            setAttachmentStatus((prev) => ({ ...prev, [row.key]: 'failed' }));
            toast.error(`Uploaded but could not resolve URL for ${row.invoiceNo || row.leaseNo}: ${describeError(error)}`);
        }
    };

    // Runs on every page load / search, not on checkbox click: uploads every
    // currently-listed invoice's PDF up front (named by Lease No), skipping
    // any lease already uploaded in this browser (see
    // getUploadedLeaseInvoiceMap above) - reusing the exact filename the
    // server returned that first time, not a reconstructed one. Sequential on
    // purpose - a page of up to 50 rows firing 50 simultaneous PDF-render+
    // upload calls would be rough on both the browser and this backend.
    const uploadInvoicesForRows = async (rows) => {
        const uploadedMap = getUploadedLeaseInvoiceMap();
        const alreadyUploaded = rows.filter((row) => row.leaseNo && uploadedMap[row.leaseNo]);
        if (alreadyUploaded.length) {
            setAttachmentStatus((prev) => {
                const next = { ...prev };
                alreadyUploaded.forEach((row) => {
                    next[row.key] = 'ready';
                });
                return next;
            });
            alreadyUploaded.forEach((row) => {
                attachmentPathByKeyRef.current[row.key] = uploadedMap[row.leaseNo];
            });
        }

        const toUpload = rows.filter((row) => !(row.leaseNo && uploadedMap[row.leaseNo]));
        for (const row of toUpload) {
            // eslint-disable-next-line no-await-in-loop
            await generateAndSaveInvoicePdf(row);
        }
    };

    // Checkbox now only marks which rows Send Email should act on - PDF
    // generation/upload already ran for the whole list on page load (see
    // uploadInvoicesForRows).
    const onSelectionChanged = () => {
        if (!gridApi) return;
        setSelectedRows(gridApi.getSelectedRows());
    };

    // Same bulk-email pattern as instalmentInvoiceListing.js / leaseGeneralInvoice/listing.js:
    // hand the selected rows off to the report server (LINVBULKSENDEMAIL) via a
    // hidden form POST into a popup, rather than building/sending the email
    // from the frontend itself.
    const sendSelectedEmails = async () => {
        setSendingEmail(true);
        if (!selectedRows?.length) {
            toast.warning('Select at least one invoice to email.');
            setSendingEmail(false);
            return;
        }
        const token = localStorage.getItem('Token');
        const DB = localStorage.getItem('DataBase');
        if (!token) {
            toast.error('Auth token is missing! Unable to proceed.');
            setSendingEmail(false);
            return;
        }

        const popupWidth = 1000;
        const popupHeight = 600;
        const popupLeft = (window.screen.width - popupWidth) / 2;
        const popupTop = (window.screen.height - popupHeight) / 2;
        const popup = window.open(
            '',
            'ReportPopup',
            `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`,
        );

        const tranId = selectedRows.map((row) => row.recordId).join('|');
        const voucherNos = selectedRows.map((row) => row.invoiceNo).join('|');
        const leaseNo = selectedRows.map((row) => row.leaseNo).join('|');
        const propertyNo = selectedRows.map((row) => row.propertyNo).join('|');
        const propertyName = selectedRows.map((row) => row.propertyName).join('|');
        const tenantNo = selectedRows.map((row) => row.tenantNo).join('|');
        const tenantName = selectedRows.map((row) => row.tenantName).join('|');
        const tenantEmailId = selectedRows.map((row) => row.tenantEmailId).join('|');
        const invoiceDate = selectedRows.map((row) => row.invoiceDate).join('|');

        if (popup) {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = REACT_APP_REPORTS;
            form.target = 'ReportPopup';

            const inputs = [
                { name: 'authToken', value: token },
                { name: '_db', value: DB },
                { name: 'ReportName', value: 'LINVBULKSENDEMAIL' },
                { name: 'TranId', value: tranId },
                { name: 'VoucherNo', value: voucherNos },
                { name: 'LeaseNo', value: leaseNo },
                { name: 'PropertyNo', value: propertyNo },
                { name: 'PropertyName', value: propertyName },
                { name: 'TenantNo', value: tenantNo },
                { name: 'TenantName', value: tenantName },
                { name: 'EmailId', value: tenantEmailId },
                { name: 'Invoicedate', value: invoiceDate },
            ];
            inputs.forEach(({ name, value }) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        }
        setSendingEmail(false);
    };

    const amountFormatter = (params) =>
        params.value === null || params.value === undefined ? '' : Number(params.value).toFixed(currencyPrecision?.decimalNumbers ?? 2);

    // AgGridReact expects columnDefs to be a stable reference across renders -
    // rebuilding this array on every render (it used to be a plain const) made
    // the grid think its columns changed on every keystroke/status update
    // (formData typing, attachmentStatus ticking generating->ready, etc.),
    // which was producing the blank/empty rows reported when clicking a
    // checkbox mid-typing or mid-generation. Only rebuild when something the
    // columns actually read (attachmentStatus, currencyPrecision) changes.
    const columnDefs = useMemo(() => [
        {
            headerName: '',
            field: 'select',
            headerCheckboxSelection: true,
            checkboxSelection: true,
            suppressMovable: true,
            sortable: false,
            filter: false,
            width: 44,
            pinned: 'left',
        },
        { headerName: 'Lease No', field: 'leaseNo', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: 'Invoice No', field: 'invoiceNo', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        {
            headerName: 'Invoice Date', field: 'invoiceDate', filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: dateFormatter, comparator: dateComparator,
        },
        { headerName: 'Customer No', field: 'tenantNo', filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: 'Customer Name', field: 'tenantName', filter: true, floatingFilter: true, suppressMovable: true, width: 220 },
        { headerName: 'Property Name', field: 'propertyName', filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
        { headerName: 'Unit No', field: 'unitNo', filter: true, floatingFilter: true, suppressMovable: true, width: 110 },
        { headerName: 'Amount', field: 'amount', filter: true, floatingFilter: true, suppressMovable: true, width: 130, valueFormatter: amountFormatter },
        {
            headerName: 'Tenant Email', field: 'tenantEmailId', filter: true, floatingFilter: true, suppressMovable: true, width: 220,
            valueFormatter: (params) => params.value || '--',
        },
        {
            headerName: 'Attachment', field: 'key', suppressMovable: true, sortable: false, filter: false, width: 130,
            valueGetter: (params) => attachmentStatus[params.data?.key],
            valueFormatter: (params) => ({ generating: 'Generating...', ready: 'Ready', failed: 'Failed' }[params.value] || '--'),
            cellStyle: (params) => {
                if (params.value === 'ready') return { color: '#1a7f37' };
                if (params.value === 'failed') return { color: '#d1242f' };
                return null;
            },
        },
    ], [attachmentStatus, currencyPrecision]);

    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                    <div className="h6 pt-1">Search Parameters</div>
                </div>
                <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                        <div className="form-group grid-contain">
                            <div className="d-flex">
                                <label className="form-label">Invoice Type</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={selectTheme}
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    value={formData.invoiceType}
                                    onChange={handleInvoiceTypeChange}
                                    options={invoiceTypeOptions}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Lease No</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={selectTheme}
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    isClearable
                                    value={formData.leaseNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'leaseNo', value: selectedOption })}
                                    options={leaseList?.map((rec) => ({ value: rec, label: rec?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Customer No</label>
                                <AsyncPaginate
                                    className="p-0 w-100"
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    isClearable
                                    value={formData.customerNo}
                                    loadOptions={loadCustomerNoOptions}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'customerNo', value: selectedOption })}
                                    additional={{ page: 0 }}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Customer Name</label>
                                <AsyncPaginate
                                    className="p-0 w-100"
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    isClearable
                                    value={formData.customerName}
                                    loadOptions={loadCustomerNameOptions}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'customerName', value: selectedOption })}
                                    additional={{ page: 0 }}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">From Date</label>
                                <DatePicker
                                    selected={formData.from}
                                    onChange={(date) => handleDateChange({ name: 'from', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">To Date</label>
                                <DatePicker
                                    selected={formData.to}
                                    onChange={(date) => handleDateChange({ name: 'to', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex gap-1">
                                {searching ? (
                                    <div className="mainbtnGry flex-end gap-2 w-50">
                                        <ClipLoader color="#ffffff" className="mt-1" size={25} />
                                    </div>
                                ) : (
                                    <div onClick={() => getItems(1)} className="mainbtnGry flex-end gap-2 w-50">
                                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                                        <div className="center">Search</div>
                                    </div>
                                )}
                                <div onClick={() => handleClearFilters()} className="mainbtnRed flex-end gap-2 w-50">
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <>
                        <div className="d-flex justify-contant-between">
                            <h5 className="col mainInvHead">Invoice Listing &amp; Email</h5>
                            <button className="mainbtn" type="button" onClick={sendSelectedEmails} disabled={sendingEmail || selectedRows.length === 0}>
                                {sendingEmail ? <ClipLoader color="#ffffff" size={18} /> : (<><IoMailOutline color="#fff" className="center mt-1" /> <div>Send Email</div></>)}
                            </button>
                        </div>
                        <div className="gridshad mt-3">
                            {/* ag-grid's own default selected-row background wins over
                                getRowStyle's alternating colors below (its theme CSS
                                targets .ag-row-selected with higher priority than the
                                per-row inline style) - override it back to something
                                visible instead of the washed-out near-white default. */}
                            <style>{`.invoice-listing-email-grid .ag-row-selected { background-color: #D9CFEA !important; }`}</style>
                            <div className="ag-theme-quartz invoice-listing-email-grid" style={{ height: 600 }}>
                                <AgGridReact
                                    onGridReady={(params) => setGridApi(params.api)}
                                    getRowStyle={(params) => {
                                        if (params.node.rowIndex % 2 === 0) {
                                            return { background: '#F0EDF5' };
                                        }
                                        return { background: '#fff' };
                                    }}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    rowSelection="multiple"
                                    suppressRowClickSelection={true}
                                    onSelectionChanged={onSelectionChanged}
                                    getRowId={(params) => params.data.key}
                                    getRowClass={(params) => (params.node.isSelected() ? 'custom-selected-row' : '')}
                                />
                            </div>
                            <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InvoiceListingEmail;
